<script setup>
/**
 * The hook table for an app that can hold several of them.
 *
 * The columns are whatever the app declares as `visible_properties`, plus the
 * inbox when hooks are per-inbox, so one table serves every multi-hook app.
 * The page header - title, search, count, `Add` - belongs to the shell above,
 * which is why the search term arrives as a model rather than being owned here.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  integrationId: {
    type: String,
    required: true,
  },
});

defineEmits(['delete']);

const { t } = useI18n();

const searchQuery = defineModel('searchQuery', { type: String, default: '' });

const { integration, isHookTypeInbox, hasConnectedHooks } = useIntegrationHook(
  props.integrationId
);

const hookHeaders = computed(() => {
  const headers = [...(integration.value.visible_properties || [])];
  if (isHookTypeInbox.value) {
    headers.push(t('INTEGRATION_APPS.LIST.INBOX'));
  }
  headers.push(t('INTEGRATION_APPS.LIST.ACTIONS'));
  return headers;
});

const hooks = computed(() => {
  if (!hasConnectedHooks.value) {
    return [];
  }
  const { hooks: integrationHooks } = integration.value;
  const visibleProperties = integration.value.visible_properties || [];
  return integrationHooks.map(hook => ({
    ...hook,
    id: hook.id,
    properties: visibleProperties.map(property =>
      hook.settings[property] ? hook.settings[property] : '--'
    ),
  }));
});

const filteredHooks = computed(() => {
  const query = searchQuery.value?.trim() || '';
  if (!query) return hooks.value;
  const lowerQuery = query.toLowerCase();
  return (
    hooks.value?.filter(hook =>
      hook.properties?.some(prop => prop?.toLowerCase().includes(lowerQuery))
    ) || []
  );
});

const inboxName = hook => (hook.inbox ? hook.inbox.name : '');
</script>

<template>
  <BaseTable
    v-if="hasConnectedHooks"
    :headers="hookHeaders"
    :items="filteredHooks"
    :no-data-message="searchQuery ? $t('INTEGRATION_APPS.NO_RESULTS') : ''"
  >
    <template #row="{ items }">
      <BaseTableRow v-for="hook in items" :key="hook.id" :item="hook">
        <template #default>
          <BaseTableCell v-for="property in hook.properties" :key="property">
            <span class="text-body-main text-n-slate-12">
              {{ property }}
            </span>
          </BaseTableCell>

          <BaseTableCell v-if="isHookTypeInbox">
            <span class="break-words text-body-main text-n-slate-11">
              {{ inboxName(hook) }}
            </span>
          </BaseTableCell>

          <BaseTableCell align="end" class="w-12">
            <div class="flex flex-shrink-0 justify-end gap-3">
              <NextButton
                v-tooltip.top="$t('INTEGRATION_APPS.LIST.DELETE.BUTTON_TEXT')"
                icon="i-woot-bin"
                slate
                sm
                class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                @click="$emit('delete', hook)"
              />
            </div>
          </BaseTableCell>
        </template>
      </BaseTableRow>
    </template>
  </BaseTable>

  <p v-else class="py-20 text-base text-center text-n-slate-11">
    {{
      $t('INTEGRATION_APPS.NO_HOOK_CONFIGURED', {
        integrationId: integration.id,
      })
    }}
  </p>
</template>
